/**
 * Individual Form UI Entry.
 */
import { Fragment, h } from 'preact';
import { useContext } from 'preact/compat';
import { DonationStepEnum } from '../hooks/step';
import renderView, { StepCondition, Theme } from '../lib/render';
import { candidateSafeGaurd } from '../hooks/candidateSafeGaurds';
import CheckoutNavigation from '../components/checkout/CheckoutNavigation';
import CheckoutForm from '../components/checkout/CheckoutForm';
import { IndividualInventory } from '../components/Inventory';
import type { Candidate } from '../types';
import Success from '../components/Success';
import IndividualShopping from '../components/IndividualIntro';
import UpsellIndividual from '../components/checkout/UpsellIndividual';
import useUrlParams from '../hooks/urlParams';

/**
 * Individual Form UI Entry.
 *
 * Features:
 * - Persistent top image, <IndividualImage />
 * - Unique shopping entry component, <IndividualShopping />
 * - Reduced inventory component, <IndividualInventory />
 * - Unique <UpsellIndividual /> component, passed as props to checkout flow.
 * - <Success /> component is 'embedded', ensures accessibility and non-page takeover styling.
 */
const Individual = () => {
	const theme = useContext(Theme);
	const candidate = theme.candidates[0] as Candidate;

	/**
	 * Param handling.
	 */
	useUrlParams([candidate], theme);

	if (!candidate) {
		return <div>Error.</div>;
	}

	return (
		<section className="ea-individual">
			{/**
			 * Ensure candidate is able to accept donations.
			 */
				candidateSafeGaurd(candidate) && (
					<Fragment>
						{/* Shopping -> Checkout -> Submit */}
						<StepCondition
							step={DonationStepEnum.SUCCESS}
							inclusive={false}
						>
							<div className="ea-individual__wrap">
								{/* Shopping entry */}
								<IndividualShopping
									theme={theme}
									candidate={candidate}
								/>
								<StepCondition
									step={DonationStepEnum.ENTRY}
									inclusive={false}
								>
									<Fragment>
										<IndividualInventory />
										<CheckoutNavigation
											initLabel="Choose Amount"
										/>
									</Fragment>
								</StepCondition>
								<CheckoutForm
									Upsell={<UpsellIndividual />}
								/>
							</div>
						</StepCondition>
						{/* Success state */}
						<StepCondition
							step={DonationStepEnum.SUCCESS}
							inclusive
						>
							<Success embedded />
						</StepCondition>
					</Fragment>
				)
			}
		</section>
	);
};

renderView(<Individual />);
